import { DEAL_FORFEIT_REASON_MAX } from '@carsfinders/contracts';
import { AlertTriangle } from 'lucide-react';
import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react';

import { t } from '../../../lib/i18n';
import { cn, formatAmount } from '../../bids/utils';
import type { DealNotice } from './deal-next-action';

export interface ForfeitDepositDialogProps {
  client: string;
  car: string;
  /** The car invoice being cancelled, when the row carries it. */
  invoiceNo: string | null;
  /** Already paid on the invoice — retained with the deposit (owner, 2026-09-28). */
  paidChf: number;
  busy: boolean;
  /** A refusal the operator can act on without leaving the dialog. */
  notice: DealNotice | null;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}

/**
 * «Утримати депозит (відмова від оплати)» — owner decision (a). A money move
 * that cannot be undone from the UI, so it asks twice: the button opens this
 * dialog, and the dialog will not confirm without a REASON (trimmed,
 * 1..{@link DEAL_FORFEIT_REASON_MAX}), which the server stores on the deal's
 * history, the invoice and the audit row.
 *
 * Keyboard: the reason field is focused on open, Esc cancels, Tab is kept
 * inside the dialog; the caller returns focus to the trigger.
 */
export function ForfeitDepositDialog({
  client,
  car,
  invoiceNo,
  paidChf,
  busy,
  notice,
  onCancel,
  onSubmit,
}: ForfeitDepositDialogProps) {
  const [reason, setReason] = useState('');
  const titleId = useId();
  const bodyId = useId();
  const reasonId = useId();
  const panelRef = useRef<HTMLDivElement | null>(null);
  const reasonRef = useRef<HTMLTextAreaElement | null>(null);
  const trimmed = reason.trim();
  const valid = trimmed.length > 0 && trimmed.length <= DEAL_FORFEIT_REASON_MAX;

  useEffect(() => {
    reasonRef.current?.focus();
  }, []);

  const onKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key === 'Escape') {
      event.stopPropagation();
      if (!busy) onCancel();
      return;
    }
    if (event.key !== 'Tab') return;
    const focusable = panelRef.current?.querySelectorAll<HTMLElement>(
      'textarea, button:not([disabled])',
    );
    if (!focusable || focusable.length === 0) return;
    const first = focusable[0]!;
    const last = focusable[focusable.length - 1]!;
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby={titleId}
      aria-describedby={bodyId}
      onKeyDown={onKeyDown}
      className="fixed inset-0 z-50 flex items-center justify-center bg-cf-ink/50 p-4"
    >
      <div
        ref={panelRef}
        className="w-full max-w-md rounded-cf-lg border border-cf-line bg-cf-surface p-6 text-left"
      >
        <div className="flex items-start gap-3">
          <AlertTriangle className="mt-0.5 shrink-0 text-cf-danger" size={20} aria-hidden />
          <div className="min-w-0">
            <h2 id={titleId} className="text-[17px] font-semibold tracking-[-0.02em]">
              {t('admin.deals.forfeit.title')}
            </h2>
            <p id={bodyId} className="mt-2 text-[13px] leading-[1.5] text-cf-ink-subtle">
              {invoiceNo
                ? t('admin.deals.forfeit.body', { client, car, no: invoiceNo })
                : t('admin.deals.forfeit.bodyNoInvoice', { client, car })}
            </p>
            {paidChf > 0 && (
              <p
                data-testid="forfeit-paid-retained"
                className="mt-2 text-[13px] leading-[1.5] font-semibold text-cf-danger"
              >
                {t('admin.deals.forfeit.paidRetained', { amount: formatAmount(paidChf) })}
              </p>
            )}
          </div>
        </div>

        <label htmlFor={reasonId} className="mt-4 block text-[12px] font-semibold text-cf-ink">
          {t('admin.deals.forfeit.reasonLabel')}
        </label>
        <textarea
          id={reasonId}
          ref={reasonRef}
          value={reason}
          rows={3}
          maxLength={DEAL_FORFEIT_REASON_MAX}
          required
          aria-required="true"
          disabled={busy}
          onChange={(event) => setReason(event.target.value)}
          placeholder={t('admin.deals.forfeit.reasonPlaceholder')}
          className="mt-1.5 w-full resize-y rounded-cf-md border border-cf-line px-3 py-2.5 text-[13px] focus-visible:outline-2 focus-visible:outline-cf-brand"
        />
        <p className="mt-1 text-right font-mono text-[11px] text-cf-ink-subtle">
          {t('admin.deals.forfeit.counter', {
            n: String(trimmed.length),
            max: String(DEAL_FORFEIT_REASON_MAX),
          })}
        </p>

        {notice && (
          <p
            role={notice.tone === 'danger' ? 'alert' : 'status'}
            className={cn(
              'mt-2 rounded-cf-md border px-2.5 py-1.5 text-[12px] leading-[1.4]',
              notice.tone === 'info'
                ? 'border-cf-info bg-cf-info-tint text-cf-info-ink'
                : 'border-cf-danger bg-cf-danger-tint text-cf-danger',
            )}
          >
            {notice.text}
          </p>
        )}

        <div className="mt-4 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            disabled={busy}
            className="h-10 rounded-cf-md border border-cf-line bg-cf-surface px-3.5 text-sm font-semibold hover:border-cf-ink focus-visible:outline-2 focus-visible:outline-cf-brand disabled:opacity-50"
          >
            {t('admin.deals.forfeit.cancel')}
          </button>
          <button
            type="button"
            onClick={() => onSubmit(trimmed)}
            disabled={!valid || busy}
            className="h-10 rounded-cf-md bg-cf-danger px-3.5 text-sm font-semibold text-white focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-cf-danger disabled:opacity-50"
          >
            {t('admin.deals.forfeit.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
}
